import React, { ReactElement } from 'react';

import { FormattedMessage } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Spinner from '@atlaskit/spinner';
import { borderRadius, colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { RelativeDate } from '@atlassian/bitkit-date';

import { useIntl } from 'src/hooks/intl';

type ActionMessages = {
  tooltipMessage: FormattedMessage.MessageDescriptor;
  makeActionLabel: FormattedMessage.MessageDescriptor;
  undoActionLabel: FormattedMessage.MessageDescriptor;
  makeActionMessage: FormattedMessage.MessageDescriptor;
};

type ReviewerActionButtonProps = {
  tabIndex?: number;
  mainIcon: ReactElement;
  isError: boolean;
  isLoading: boolean;
  messages: ActionMessages;
  isSelected?: boolean;
  isDisabled: boolean;
  actionDate?: string | Date;
  makeAction: () => void;
  undoAction: () => void;
  removeRightBorderRadius?: boolean;
  removeLeftBorderRadius?: boolean;
};

export const ReviewerActionButton = ({
  tabIndex,
  mainIcon,
  isError,
  isLoading,
  messages,
  isSelected,
  isDisabled,
  actionDate,
  makeAction,
  undoAction,
  removeRightBorderRadius,
  removeLeftBorderRadius,
}: ReviewerActionButtonProps) => {
  const intl = useIntl();
  const buttonTheme = (currentTheme: any, props: any) => ({
    ...currentTheme(props),
    buttonStyles: {
      ...currentTheme(props).buttonStyles,
      borderRadius: removeRightBorderRadius
        ? `${borderRadius()}px 0 0 ${borderRadius()}px`
        : removeLeftBorderRadius
        ? `0 ${borderRadius()}px ${borderRadius()}px 0`
        : `${borderRadius()}px`,
    },
  });
  let icon = mainIcon;
  if (isError) {
    icon = (
      <WarningIcon
        label=""
        size="medium"
        primaryColor={token('color.icon.warning', colors.Y300)}
        testId="warning-icon"
      />
    );
  }
  if (isLoading) {
    icon = <Spinner size="small" delay={0} testId="spinner" />;
  }

  if (isSelected) {
    const message = actionDate ? (
      <FormattedMessage
        {...messages.tooltipMessage}
        values={{
          date: <RelativeDate date={actionDate} />,
        }}
      />
    ) : (
      ''
    );

    return (
      <Tooltip position="top" content={message}>
        <CustomThemeButton
          appearance="default"
          aria-pressed="true"
          isSelected
          aria-label={intl.formatMessage(messages.undoActionLabel)}
          iconBefore={icon}
          isDisabled={isDisabled}
          onClick={undoAction}
          tabIndex={tabIndex}
          theme={buttonTheme}
        >
          <FormattedMessage {...messages.makeActionMessage} />
        </CustomThemeButton>
      </Tooltip>
    );
  }

  return (
    <CustomThemeButton
      appearance="default"
      aria-pressed="false"
      isSelected={false}
      aria-label={intl.formatMessage(messages.makeActionLabel)}
      iconBefore={icon}
      isDisabled={isDisabled}
      onClick={makeAction}
      tabIndex={tabIndex}
      theme={buttonTheme}
    >
      <FormattedMessage {...messages.makeActionMessage} />
    </CustomThemeButton>
  );
};

export default React.memo(ReviewerActionButton);
